import { useMemo } from "react";

import {
  useLevelContext,
  type Level,
  type LevelPath,
} from "sj-ui-toolkit/src/atlas/contexts/Level/LevelContextProvider";
import type { Enrollment } from "sj-ui-toolkit/src/api/userEnrollments";
import { BucketOfLearninRow } from "./BucketOfLearninRow";
import { getLevelVariant } from "../../../../helpers/levelVariants";
import { getEnrolledRoleIds } from "../../../../helpers/learnins";
import { sortByEnrollment } from "../../../../helpers/sortByEnrollment";

interface LevelRowProps {
  metadata: Level;
  details?: LevelPath[];
  currentPaths?: Enrollment[];
  completed: boolean;
}

export const LevelRow = ({ metadata, details, currentPaths = [], completed }: LevelRowProps) => {
  const { isLoading: contextLoading } = useLevelContext();

  // Completed levels: show every path as-is. Current levels: enrolled paths first,
  // then the rest. See sortByEnrollment for ordering details.
  const filteredDetails = useMemo(() => {
    if (completed) return details;
    return sortByEnrollment(details, getEnrolledRoleIds(currentPaths));
  }, [details, currentPaths, completed]);

  const isLoading =
    contextLoading ||
    details === undefined ||
    (details !== undefined && filteredDetails === undefined);

  return (
    <BucketOfLearninRow
      loading={isLoading}
      id={metadata.id}
      name={metadata.name}
      description={metadata.description}
      learnins={filteredDetails}
      variant={getLevelVariant(metadata)}
      viewAllHref={`/levels/${metadata.id}`}
      completed={completed}
    />
  );
};

export const LevelPaths = ({ currentPaths }: { currentPaths?: Enrollment[] }) => {
  const { levels, isLoading, levelDetails } = useLevelContext();

  if (isLoading) {
    return null;
  }

  const visibleLevels = levels.filter(level => !["brown", "black"].includes(level.rankName));

  const renderRow = (level: Level, completed: boolean) => (
    <LevelRow
      key={level.id}
      metadata={level}
      details={levelDetails[level.id]}
      currentPaths={currentPaths}
      completed={completed}
    />
  );

  const currentLevels = visibleLevels.filter(level => level.status !== "passed");
  const completedLevels = visibleLevels.filter(level => level.status === "passed");

  return (
    <div className="my-8">
      {currentLevels.length > 0 && (
        <section className="mb-8">
          <h2 className="atlas-h4 mb-4">
            {currentLevels.length === 1 ? "Current Level" : "Current Levels"}
          </h2>
          {currentLevels.map(level => renderRow(level, false))}
        </section>
      )}
      {completedLevels.length > 0 && (
        <section>
          <h2 className="atlas-h4 mb-4">
            {completedLevels.length === 1 ? "Completed Level" : "Completed Levels"}
          </h2>
          {completedLevels.map(level => renderRow(level, true))}
        </section>
      )}
    </div>
  );
};
